import * as tslib_1 from "tslib";
import { EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { BehaviorSubject, Subject, defer, from, of } from 'rxjs';
import { catchError, map, switchMap, take, timeout } from 'rxjs/operators';
import { AppService } from './app.service';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { SignalRService } from './signalr.service';
import { isPlayerStateNullState } from './player-state-error';
import { RequestsType } from '../model/appConfig';
import { ErrorCode } from '../model/errorcode';
import { State, toStateArray } from '../model/state.model';
import { Platform } from '@ionic/angular';
var PlayerService = /** @class */ (function () {
    function PlayerService(router, authService, apiService, appService, platform, alertController, signalRService) {
        var _this = this;
        this.router = router;
        this.authService = authService;
        this.apiService = apiService;
        this.appService = appService;
        this.platform = platform;
        this.alertController = alertController;
        this.signalRService = signalRService;
        this.interval = 0;
        this.ready = new BehaviorSubject(false);
        this.state = (new State()).init();
        this.prevTimeSong = 0;
        this.isPlay = false;
        this.commands = [];
        this.MAX_COMMAND_SEND = 10;
        this.playlistLoaded = new BehaviorSubject(false);
        /** Emitted when a state packet includes `playlists` (command 99 / full sync). Used to resolve SignalR playlist fetch. */
        this.playlistsArrived = new Subject();
        this.errCounter = 0;
        this.songChanged = new BehaviorSubject(false);
        this.noResults = false;
        this.deltaVolume = new BehaviorSubject(null);
        this.volumeChanged = new EventEmitter();
        this.platformPauseSubject = new BehaviorSubject(false);
        if (this.authService.authenticated) {
            this.start().catch(function (error) {
                console.error('Failed to start player service:', error);
            });
        }
        this.platformPauseSubject.subscribe(function (pauseValue) {
            // on open app from background force to download all information in sync()
            if (pauseValue === false) {
                console.log('App resumed - resetting error counter and forcing sync');
                _this.errCounter = 0; // Reset error counter on resume
                _this.commands.push({ id: 99 });
            }
            else {
                console.log('App paused - maintaining background sync');
            }
        });
        this.signalRService.statesToProcess.subscribe(function (states) {
            if (states !== null) {
                _this.signalRService.statesToProcess.next(null);
                // "MyDjPlayerState_<id> is null" means the player has not reported any state yet
                // (offline / just started). The SignalR listener already drops such packets; the
                // HTTP (JSONP) transport used by the mobile builds delivers them here, and they
                // must not surface as an "Unknown error" popup. The server did answer, though,
                // so the connection is healthy - keep the error counter reset.
                var actionableStates = toStateArray(states).filter(function (state) { return !isPlayerStateNullState(state); });
                _this.errCounter = 0;
                if (actionableStates.length === 0) {
                    return;
                }
                actionableStates.forEach(function (state) { _this.buildState(state); });
                _this.ready.next(true);
            }
        });
    }
    PlayerService.prototype.start = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var command, config, error_1;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.errCounter = 0;
                        command = JSON.stringify({ "id": 99 });
                        this.appService.showNotification("connecting...");
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.appService.getConfig()];
                    case 2:
                        config = _a.sent();
                        if (config.requestsType === RequestsType.SignalR) {
                            this.signalRService.sendMessage(this.authService.currentUser.name, command);
                        }
                        else {
                            this.apiService.sendCommand(this.authService.currentUser, command)
                                .subscribe(function (states) {
                                _this.signalRService.statesToProcess.next(states);
                            });
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_1 = _a.sent();
                        console.error('Failed to get config during start:', error_1);
                        // Fallback to HTTP mode
                        this.apiService.sendCommand(this.authService.currentUser, command)
                            .subscribe(function (states) {
                            _this.signalRService.statesToProcess.next(states);
                        });
                        return [3 /*break*/, 4];
                    case 4:
                        this.interval = window.setInterval(function () {
                            _this.sync();
                        }, 1000);
                        return [2 /*return*/];
                }
            });
        });
    };
    PlayerService.prototype.stop = function () {
        clearInterval(this.interval);
        this.volumeChanged.emit({ volume: 0, force: true });
        this.state = (new State()).init();
        // Ensure the SignalR connection is closed so the next start connects with the current user
        try {
            this.signalRService.disconnect();
        }
        catch (err) {
            console.error('Error while disconnecting SignalR during stop:', err);
        }
    };
    PlayerService.prototype.sync = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var command, count, firstCommands, config, error_2, err_1;
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 5, , 6]);
                        command = JSON.stringify({ id: this.state.currentSong.name === "" ? 99 : 0 });
                        if (this.state.currentSong.name === "") {
                            this.appService.showNotification("connecting...");
                        }
                        count = this.commands.filter(function (c) { return c.id === 99; }).length;
                        if (count > 1) {
                            this.commands = this.commands.filter(function (c) { return c.id !== 99; });
                            this.commands.push({ id: 99 });
                        }
                        if (this.commands.length > 0) {
                            if (this.commands.length > this.MAX_COMMAND_SEND) {
                                firstCommands = this.commands.slice(0, this.MAX_COMMAND_SEND);
                                this.commands = this.commands.slice(this.MAX_COMMAND_SEND);
                                command = JSON.stringify(firstCommands);
                            }
                            else {
                                command = JSON.stringify(this.commands);
                                this.commands = [];
                            }
                        }
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this.appService.getConfig()];
                    case 2:
                        config = _a.sent();
                        if (config.requestsType === RequestsType.SignalR) {
                            this.signalRService.sendMessage(this.authService.currentUser.name, command);
                        }
                        else {
                            this.apiService.sendCommand(this.authService.currentUser, command)
                                .subscribe(function (states) {
                                _this.signalRService.statesToProcess.next(states);
                            });
                        }
                        return [3 /*break*/, 4];
                    case 3:
                        error_2 = _a.sent();
                        console.error('Failed to get config during sync:', error_2);
                        // Fallback to HTTP mode
                        this.apiService.sendCommand(this.authService.currentUser, command)
                            .subscribe(function (states) {
                            _this.signalRService.statesToProcess.next(states);
                        });
                        return [3 /*break*/, 4];
                    case 4: return [3 /*break*/, 6];
                    case 5:
                        err_1 = _a.sent();
                        console.error('Sync error:', err_1);
                        this.appService.loading = "";
                        // Only increment error counter if not in background
                        if (!this.platformPauseSubject.value) {
                            this.errCounter++;
                            this.appService.showNotification("connection problem");
                        }
                        else {
                            console.log('Background sync error (not counting):', err_1);
                        }
                        // Increased threshold to prevent premature logout
                        if (this.errCounter > 50) {
                            console.error('Too many errors, logging out');
                            this.stop();
                            this.authService.logout();
                            this.router.navigateByUrl("/auth/login");
                        }
                        return [3 /*break*/, 6];
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    PlayerService.prototype.buildState = function (state) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var errorHeader, errorMessage, alert_1, field, err_2;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 4, , 5]);
                        if (!(state.errorCode && !isPlayerStateNullState(state))) return [3 /*break*/, 3];
                        errorHeader = "Error";
                        errorMessage = void 0;
                        switch (state.errorCode) {
                            case ErrorCode.AuthFailed:
                                errorMessage = state.error;
                                break;
                            case ErrorCode.NoPermissionForCommand:
                                errorHeader = null;
                                errorMessage = "No permission for command " + state.error;
                                break;
                            default:
                                errorMessage = "Unknown error " + state.error;
                                break;
                        }
                        return [4 /*yield*/, this.alertController.create({
                                header: errorHeader,
                                message: errorMessage,
                                buttons: ['OK'],
                            })];
                    case 1:
                        alert_1 = _a.sent();
                        return [4 /*yield*/, alert_1.present()];
                    case 2:
                        _a.sent();
                        _a.label = 3;
                    case 3:
                        this.prevTimeSong = this.state.currentTimeSong;
                        for (field in state) {
                            if (field === "playlistSongs") {
                                this.state[field] = this.state[field].concat(state[field]);
                            }
                            else {
                                this.state[field] = state[field];
                            }
                            if (field === "volume") {
                                this.volumeChanged.emit({ volume: state[field] });
                            }
                        }
                        if (state.scheduler !== undefined) {
                            this.state.scheduler = state.scheduler;
                        }
                        if (state.playlists !== undefined && state.playlists !== null) {
                            this.playlistsArrived.next(state.playlists);
                        }
                        this.isPlay = this.state.currentTimeSong !== this.prevTimeSong;
                        if (state.searchResultsList) { // switch off preloader when got search result         
                            this.appService.loading = "";
                            this.noResults = !state.searchResultsList.length;
                        }
                        if (state.playlistSongs && state.playlistSongs.length) { // switch off preloader when got playlist songs
                            this.appService.loading = "";
                            this.playlistLoaded.next(true);
                        }
                        // if currentSong changed then request full info to update current playlist
                        if (state.currentSong && state.volume === undefined) {
                            this.commands.push({ id: 99 });
                            this.songChanged.next(true);
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        err_2 = _a.sent();
                        console.error('Error in buildState:', err_2);
                        return [3 /*break*/, 5];
                    case 5: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Loads playlists via the same command as full sync (id 99). HTTP: jsonp APIPhonePlayer; SignalR: hub message, then wait for playlists in state.
     */
    PlayerService.prototype.requestPlaylistsList = function (user) {
        var _this = this;
        var cmd = JSON.stringify({ id: 99 });
        var parsePlaylistsFromStates = function (states) {
            var withPlaylists = toStateArray(states).find(function (s) { return s && Array.isArray(s.playlists); });
            var list = withPlaylists && Array.isArray(withPlaylists.playlists)
                ? withPlaylists.playlists
                : [];
            return list.length > 0 ? list : null;
        };
        return from(this.appService.getConfig()).pipe(switchMap(function (config) {
            if (config.requestsType === RequestsType.SignalR) {
                // Подписка на playlistsArrived должна быть активна до sendMessage, иначе быстрый ответ теряется.
                return defer(function () {
                    var stream = _this.playlistsArrived.pipe(take(1), timeout(15000), catchError(function () { return of(null); }));
                    queueMicrotask(function () { return _this.signalRService.sendMessage(user.name, cmd); });
                    return stream;
                });
            }
            return _this.apiService.sendCommand(user, cmd).pipe(map(function (states) { return parsePlaylistsFromStates(states); }), catchError(function () { return of(null); }));
        }));
    };
    PlayerService.prototype.setVolume = function (v) {
        this.appService.showNotification("sending [volume]");
        var i = this.commands.findIndex(function (c) { return c.id === 8; });
        if (i === -1) {
            this.commands.push({ id: 8, volume: v });
            this.commands.push({ id: 99 });
        }
        else {
            this.commands[i] = { id: 8, volume: v };
        }
    };
    PlayerService.prototype.play = function () {
        this.appService.showNotification("sending [play]");
        this.commands.push({ id: 1 });
    };
    PlayerService.prototype.pause = function () {
        this.appService.showNotification("sending [pause]");
        this.commands.push({ id: 2 });
    };
    PlayerService.prototype.nextTrack = function () {
        this.appService.showNotification("sending [next]");
        this.commands.push({ id: 3 });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.happyBirthday = function () {
        this.appService.showNotification("sending [birthday]");
        this.commands.push({ id: 6 });
    };
    PlayerService.prototype.setShuffle = function (value) {
        this.appService.showNotification("sending [shuffle]");
        this.commands.push({ id: 7, shuffle: value ? "on" : "off" });
        this.commands.push({ id: 99 }); // после некоторых команд добавлена 99-я, чтобы принудительно получить статус от сервера       
    };
    PlayerService.prototype.seek = function (seekTime) {
        this.appService.showNotification("sending [seek]");
        this.commands.push({ id: 9, sec: seekTime });
    };
    PlayerService.prototype.search = function (s) {
        this.appService.showNotification("sending [search]");
        this.commands.push({ id: 14, searchString: s });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.remove = function (songID) {
        this.appService.showNotification("sending [remove]");
        this.commands.push({ id: 15, songID: songID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.deleteForever = function (songID) {
        this.appService.showNotification("sending [delete forever]");
        this.commands.push({ id: 27, songID: songID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.playNext = function (songID) {
        this.appService.showNotification("sending [play next]");
        this.commands.push({ id: 16, songID: songID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.playNow = function (songID) {
        this.appService.showNotification("sending [play now]");
        this.commands.push({ id: 17, songID: songID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.setPlaylist = function (playlistID) {
        this.appService.showNotification("sending [set playlist]");
        this.commands.push({ id: 26, playlistID: playlistID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.addPlaylist = function (playlistID) {
        this.appService.showNotification("sending [add playlist]");
        this.commands.push({ id: 35, playlistID: playlistID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.getPlaylistSongs = function (playlistID) {
        this.appService.showNotification("sending [get playlist songs]");
        this.commands.push({ id: 13, playlistID: playlistID });
    };
    PlayerService.prototype.playlistSongPlayNow = function (playlistID, songID) {
        this.appService.showNotification("sending [playlist song - play now]");
        this.commands.push({ id: 22, playlistID: playlistID, songID: songID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.playlistSongPlayNext = function (playlistID, songID) {
        this.appService.showNotification("sending [playlist song - play next]");
        this.commands.push({ id: 21, playlistID: playlistID, songID: songID });
        this.commands.push({ id: 99 });
    };
    // в поисковом списке свои идентификаторы, поэтому для манипуляций с песнями из списка найденных существуют отдельные функции
    PlayerService.prototype.playNextAfterSearch = function (songID) {
        this.appService.showNotification("sending [play next]");
        this.commands.push({ id: 23, songID: songID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.playNowAfterSearch = function (songID) {
        this.appService.showNotification("sending [play now]");
        this.commands.push({ id: 24, songID: songID });
        this.commands.push({ id: 99 });
    };
    // additional Newyear functions
    PlayerService.prototype.ny1 = function () {
        this.appService.showNotification("sending [ny1]");
        this.commands.push({ id: 28 });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.ny2 = function () {
        this.appService.showNotification("sending [ny2]");
        this.commands.push({ id: 29 });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.UpdateLikeStatusOfPlaylist = function (playlistId, likeType) {
        this.appService.showNotification("sending like status");
        this.commands.push({ id: 33, "playlistID": playlistId, "likeType": likeType });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.UpdateLikeStatusOfSong = function (page, playlistId, songId, typeLike) {
        this.appService.showNotification("sending like status");
        this.commands.push({ id: 32, page: page, playlistID: playlistId, songID: songId, likeType: typeLike });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.AllHeartsNextInMainPlaylist = function () {
        this.appService.showNotification("sending All hearts Next in the main PL");
        this.commands.push({ id: 34 });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.AllHeartsNextInSpecificPlaylist = function (playlistID) {
        this.appService.showNotification("sending All hearts Next from a specific PL");
        this.commands.push({ id: 30, playlistID: playlistID });
        this.commands.push({ id: 99 });
    };
    PlayerService.prototype.AllHeartsNextInTheSearchResults = function () {
        this.appService.showNotification("sending All hearts Next from a specific PL");
        this.commands.push({ id: 31 });
        this.commands.push({ id: 99 });
    };
    return PlayerService;
}());
export { PlayerService };
